/*
 * Design note for this page:
 * Cybernetic Editorial Observatory applied to commercial policy.
 * Use a calm but strict tone that makes cancellation rights, manual billing handling,
 * and the no-refund rule after rendered analysis unmistakably clear.
 */

import PolicyLayout from "@/components/PolicyLayout";
import { BadgeDollarSign, FileLock2, RotateCcw } from "lucide-react";

const cards = [
  {
    title: "Cancellation remains available",
    body: "Customers are allowed to request cancellation of a plan or recurring service path. The page is designed to make that right visible instead of forcing users into a hidden or unclear billing loop.",
    icon: RotateCcw,
  },
  {
    title: "No refund after rendered analysis",
    body: "Where paid analytical work, individualized outputs, or report-delivery work has already been rendered, payments are treated as non-refundable because the digital service has already been performed.",
    icon: FileLock2,
  },
  {
    title: "Manual billing handling",
    body: "Upgrade, downgrade, subscription correction, and billing clarification requests are expected to be handled manually through the public support path until a dedicated self-serve billing console is publicly released.",
    icon: BadgeDollarSign,
  },
];

const sections = [
  {
    id: "cancellation-right",
    title: "How cancellation works",
    intro:
      "Command Secrets allows customers to request cancellation of subscriptions, future renewals, or continuing paid access, subject to the commercial and operational boundaries described on this page.",
    paragraphs: [
      "A cancellation request generally means that future billing or future access renewal should stop from the next appropriate billing point, not that previously delivered services are automatically reversed. The exact effective date may depend on the current plan, billing cycle, outstanding charges, active support work, and whether the request is made before or after a renewal event has already been processed.",
      "Customers who wish to cancel should use the support path and provide enough identifying information for the account to be located safely. Command Secrets may verify identity before acting on cancellation instructions so that a third party cannot interfere with another customer's account or subscription.",
      "Where the service later introduces direct in-account cancellation controls, those controls may operate together with this public policy and the platform-specific billing rules that apply at the time.",
    ],
  },
  {
    id: "no-refund",
    title: "Strict no-refund rule after analytical work is rendered",
    intro:
      "Command Secrets treats rendered digital analysis as completed intellectual and computational work, not as an untouched physical good that can simply be returned.",
    paragraphs: [
      "If a paid analysis, individualized report, premium insight, consultation preparation, or similar deliverable has already been generated, exposed, delivered, downloaded, or materially rendered for the customer, the relevant payment is non-refundable unless Command Secrets explicitly states otherwise in writing for that specific case.",
      "This rule exists because the value of the service lies in the customized analytical work, report generation, interpretation layer, and proprietary computational process itself. Once that work has been performed or made available, the essential service has already been supplied even if the customer later changes their mind.",
      "Nothing on this page should be interpreted as waiving any non-excludable legal rights that may apply under mandatory consumer law. However, to the fullest extent permitted by law, customers agree that completed and rendered analytical work is not eligible for refund on the basis of preference, regret, changed intention, or non-use after delivery.",
    ],
  },
  {
    id: "upgrades-downgrades",
    title: "Upgrade, downgrade, and billing-adjustment requests",
    intro:
      "Customers may request plan changes, but those changes may require manual review before taking effect.",
    paragraphs: [
      "If a user wants to move to a higher plan, lower plan, yearly access, lifetime path, or corrected billing arrangement, the request should be submitted through the support page together with sufficient account details for verification. Command Secrets may process the change prospectively, at the next renewal, or through another commercially reasonable method depending on what has already been billed or delivered.",
      "Where a plan upgrade triggers additional individualized analysis, premium reports, or extended history access, the resulting new work may become subject to the same no-refund rule once that additional service is rendered.",
      "If a billing correction is required because of duplicate payment, obvious technical fault, or platform processing error, Command Secrets may investigate the issue, request supporting records, and determine the appropriate correction under the relevant payment-provider and legal framework.",
    ],
  },
  {
    id: "deliverables",
    title: "Reports, watermarking, and post-delivery boundaries",
    intro:
      "Report delivery is treated as a protected intellectual-property event, not merely as a disposable file transfer.",
    paragraphs: [
      "Where PDF or similar report files are delivered, Command Secrets may use watermarking, account-linked branding, access controls, download logging, and other protective measures to preserve authorship, reduce unauthorised redistribution, and discourage resale or copying of proprietary analytical output.",
      "Customers are responsible for safeguarding any report they choose to download, store, forward, or share. Command Secrets does not accept responsibility for exposure caused by a user's own onward sharing, forwarding, screenshotting, or insecure local storage after lawful delivery has taken place.",
      "If a customer needs a resend, billing clarification, or access restoration after a completed order, the request should be submitted through support so the service team can verify the request and maintain a reliable delivery record.",
    ],
  },
];

const relatedLinks = [
  {
    label: "Support",
    href: "/support",
    description: "Use the manual support path for cancellations, billing clarification, plan changes, and report-delivery help.",
  },
  {
    label: "Privacy notice",
    href: "/legal/privacy",
    description: "See how cancellation and billing requests are handled together with identity verification and data-protection safeguards.",
  },
  {
    label: "Disclaimer",
    href: "/legal/disclaimer",
    description: "Review the legal boundaries around illustrative references, estimation limits, and protected automated analysis surfaces.",
  },
  {
    label: "Security",
    href: "/security",
    description: "Understand the service's anti-abuse posture, owner-access controls, and resilience expectations around protected reports and customer data.",
  },
];

export default function Cancellation() {
  return (
    <PolicyLayout
      eyebrow="Cancellation policy"
      title="Customers can cancel future access, but rendered digital analysis is treated as completed work and is not refundable."
      intro="This page explains how Command Secrets handles cancellation, plan changes, billing support, and the strict no-refund rule once individualized analytical work has already been rendered, delivered, or made available to the customer."
      highlight="Clear cancellation rights and clear no-refund boundaries prevent billing ambiguity."
      cards={cards}
      sections={sections}
      relatedLinks={relatedLinks}
    />
  );
}
